/**
 * 把 `str` 中的 "=", "&" 替换成 Url 编码形式, 避免破坏 Map 字符串结构
 * @example
 * ```js
 * escapeMapString("ab");       // -> "ab"
 * escapeMapString("a=b");      // -> "a%3Db"
 * escapeMapString("a=b&c=d");  // -> "a%3Db%26c%3Dd"
 * ```
 */
export function escapeMapString(str: string): string {
  return str.replace(/=/g, '%3D').replace(/&/g, '%26');
}

/**
 * 将 K1=V1&K2=V2 形式的 Map 字符串转换成对象
 *
 * `parseMap` 直接使用 "&", "=" 分割字符串, 不进行任何解码.
 * 如果需要 Url 解码, 请使用 [[parseQuery]]. 如果需要更复杂的转换, 请使用第三方库 [qs](https://github.com/ljharb/qs).
 * @returns 返回解析后的对象
 * @example
 * ```js
 * parseMap("a=1&b=&foo=a+b&emoji=%26%5E%26");     // -> { a: "1", b: "", foo: "a+b", emoji: "%26%5E%26" }
 * ```
 */
export function parseMap(mapStr: string): Record<string, string> {
  const result: Record<string, string> = {};
  mapStr.split('&').forEach((pair) => {
    const [key, value = ''] = pair.split('=');
    if (!key || key in result) return;

    result[key] = value;
  });

  return result;
}

/**
 * 将对象转换成用 K1=V1&K2=V2 的 Map 字符串编码形式
 *
 * `stringifyMap` 直接将对象的键和值转换成字符串后, 用 "=", "&" 连接.
 * 除了使用 [[escapeMapString]] 将键值字符串中的 "=", "&" 两个字符转义, 其它不进行任何编码.
 * 如果需要 Url 编码, 请使用 [[stringifyQuery]]. 如果需要更复杂的转换, 请使用第三方库 [qs](https://github.com/ljharb/qs).
 * @returns 返回编码后的字符串，其中 `undefined`、`null`、`NaN` 值会被替换成空字符串
 * @description 基于 [querystringify](https://github.com/unshiftio/querystringify/blob/master/index.js#L75) 修改而来
 * @example
 * ```js
 * stringifyMap({ foo: "bar", bar: "baz" });            // -> "foo=bar&bar=baz"
 * stringifyMap({ n: 1, b: true });                     // -> "n=1&b=true"
 * stringifyMap({ null: null, undef: undefined });      // -> "null=&undef="
 * stringifyMap({ o: {} });                             // -> "o=[object Object]"
 * stringifyMap({ emo: "(*^▽^*)", ch: "中文" });       // -> "emo=(*^▽^*)&ch=中文"
 * ```
 */
export function stringifyMap(data: Record<any, any>): string {
  const pairs: string[] = [];
  Object.keys(data).forEach((key) => {
    let value = data[key];
    if (!value && (value === null || value === undefined || isNaN(value))) {
      value = '';
    }

    pairs.push(`${escapeMapString(String(key))}=${escapeMapString(String(value))}`);
  });

  return pairs.length ? pairs.join('&') : '';
}
